import { Button, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../../../../../../api/biApi";
import type { MeasureDescriptor } from "../../../../../../../../../api/types/svTypes.ts";
import { selectMeasureSubgroups } from "../../../../../store/selectors/metaDataSelectors";
import { createMetaDataAction } from "../../../../../store/utils/storeUtils";
import { AlertItem } from "../../../../common/AlertItem";
import { resolveMeasureSubgroupId } from "../utilities/measureSubgroups";
import SubgroupSelect from "./SubgroupSelect";

const moveCustomMeasureToSubgroup = withErrorHandling(biApi.moveCustomMeasureToSubgroup);

interface Props {
  measure: MeasureDescriptor;
  onClose: () => void;
}

const MoveToSubgroupDialog = ({ measure, onClose }: Props) => {
  const dispatch = useDispatch();
  const { sendNotification } = useNotificationContext();
  const measureSubgroups = useSelector(selectMeasureSubgroups);

  const [selectedSubgroupId, setSelectedSubgroupId] = useState(() =>
    resolveMeasureSubgroupId(measure, measureSubgroups)
  );
  const [saving, setSaving] = useState(false);
  const [savingError, setSavingError] = useState<string>();

  const currentSubgroupId = resolveMeasureSubgroupId(measure, measureSubgroups);
  const canApply = selectedSubgroupId !== currentSubgroupId && !saving;

  const handleSubgroupChange = (subgroupId: string | undefined) => {
    setSelectedSubgroupId(subgroupId);
    setSavingError(undefined);
  };

  const handleApply = async () => {
    setSaving(true);
    setSavingError(undefined);
    const [movedMeasure, error] = await moveCustomMeasureToSubgroup(
      measure.measureId ?? measure.name,
      selectedSubgroupId
    );
    setSaving(false);

    if (error) {
      logError(error, "[MoveToSubgroupDialog] moveCustomMeasureToSubgroup");
      setSavingError(error.message);
      return;
    }

    dispatch(createMetaDataAction("updateMeasure", movedMeasure));
    sendNotification("Measure moved successfully");
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle noWrap sx={{ mr: 2 }}>
        Move {measure.caption} to Subgroup
      </DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2}>
          {savingError && <AlertItem error={savingError} />}
          <SubgroupSelect
            group={measure.group}
            subgroupId={selectedSubgroupId}
            disabled={saving}
            onChange={handleSubgroupChange}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={saving}>
        <Button variant="contained" disabled={!canApply} loading={saving} onClick={handleApply}>
          Apply
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default MoveToSubgroupDialog;
